/**
 * Mapeador de mensagens técnicas do NestJS ValidationPipe para mensagens
 * amigáveis em PT-BR exibidas no wizard de Configuração do Condomínio.
 *
 * Recebe o `data.message` cru do backend (string | string[] | objeto) e devolve
 * uma mensagem que o usuário entende + o array técnico preservado para o Sentry.
 */

export interface FriendlyError {
  /** Mensagem amigável em PT-BR pronta para exibir. */
  friendly: string;
  /** Mensagens técnicas originais (preservadas para Sentry/log). */
  technical: string[];
  /** Caminho do campo culpado, quando identificável (ex.: "syndic.companyName"). */
  field?: string;
  /** Sugestão de ação adicional. */
  suggestion?: string;
}

/** Traduz tokens do payload para rótulos PT-BR usados em mensagens ao usuário. */
const fieldLabels: Record<string, string> = {
  // raiz
  name: 'Nome do condomínio',
  document: 'CNPJ do condomínio',
  cep: 'CEP',
  address: 'Endereço',
  neighborhood: 'Bairro',
  city: 'Cidade',
  state: 'Estado',
  contactPhone: 'Telefone de contato',
  contactEmail: 'E-mail de contato',
  website: 'Site',
  foundationDate: 'Data de fundação',
  type: 'Tipo do condomínio',
  totalApartments: 'Total de apartamentos',
  totalCommercialUnits: 'Total de unidades comerciais',
  totalFloors: 'Total de andares',
  totalGarageFloors: 'Total de andares de garagem',
  // sub-objetos
  syndic: 'Síndico',
  subSyndic: 'Subsíndico',
  janitor: 'Zelador',
  administrator: 'Administradora',
  securityCompany: 'Empresa de segurança',
  cleaningCompany: 'Empresa de limpeza',
  erpSystem: 'Sistema ERP',
  elevatorCompany: 'Empresa de elevadores',
  infrastructure: 'Infraestrutura',
  maintenanceSetup: 'Plano de manutenção',
  // campos comuns
  cpf: 'CPF',
  cnpj: 'CNPJ',
  email: 'E-mail',
  phone: 'Telefone',
  apartment: 'Apartamento',
  companyName: 'Razão Social',
  contact: 'Contato',
};

function humanizePath(path: string): string {
  // "syndic.companyName" -> "Síndico → Razão Social"
  // "infrastructure.saunas.quantity" -> "Infraestrutura → Sauna → Quantidade"
  return path
    .split('.')
    .map((segment) => fieldLabels[segment] ?? segment)
    .join(' → ');
}

/** Normaliza `raw` para um array de strings, lidando com formatos variados. */
function normalizeRaw(
  raw: string | string[] | Record<string, unknown> | undefined | null,
): string[] {
  if (raw == null) return [];
  if (typeof raw === 'string') return [raw];
  if (Array.isArray(raw)) {
    return raw.filter((m): m is string => typeof m === 'string');
  }
  if (typeof raw === 'object') {
    // Resposta inesperada (ex: { error: 'Bad Request', detail: {...} }).
    // Tentar extrair algum campo textual; caso contrário, serializar.
    const record = raw as Record<string, unknown>;
    const candidate = record['message'] ?? record['error'] ?? record['detail'];
    if (typeof candidate === 'string') return [candidate];
    if (Array.isArray(candidate)) {
      return candidate.filter((m): m is string => typeof m === 'string');
    }
    try {
      return [JSON.stringify(raw)];
    } catch {
      return [];
    }
  }
  return [];
}

/** Map de padrões → builder de mensagem amigável. */
type Rule = {
  test: RegExp;
  build: (match: RegExpMatchArray) => Pick<FriendlyError, 'friendly' | 'field' | 'suggestion'>;
};

const rules: Rule[] = [
  // 1) Campos não-whitelisted (ex.: "syndic.property companyName should not exist").
  //    Captura paths aninhados (qualquer nº de níveis antes de ".property").
  {
    test: /^(?:([\w.]+)\.)?property\s+([\w]+)\s+should not exist$/,
    build: (m) => {
      const parent = m[1];
      const child = m[2];
      const field = parent ? `${parent}.${child}` : child;
      return {
        friendly:
          'Tivemos um problema técnico ao salvar seus dados. Nosso time já foi notificado. Recarregue a página e tente novamente.',
        field,
        suggestion: 'Se persistir, limpe o cache do navegador para esta página.',
      };
    },
  },
  // 2) CPF inválido (validator do backend já manda em PT-BR — só removemos prefixo).
  {
    test: /^(?:([\w.]+)\.)?CPF inválido/i,
    build: (m) => ({
      friendly: 'CPF inválido. Verifique se os dígitos estão corretos.',
      field: m[1] ? `${m[1]}.cpf` : 'cpf',
    }),
  },
  // 3) CNPJ inválido / fora do formato.
  {
    test: /^(?:([\w.]+)\.)?CNPJ\s+(inválido|deve estar)/i,
    build: (m) => ({
      friendly: 'CNPJ inválido. Use o formato 00.000.000/0000-00.',
      field: m[1] ? `${m[1]}.cnpj` : 'cnpj',
    }),
  },
  // 4) CEP fora do formato.
  {
    test: /^CEP\s+deve estar no formato/i,
    build: () => ({
      friendly: 'CEP inválido. Use o formato 00000-000.',
      field: 'cep',
    }),
  },
  // 5) email obrigatório vazio.
  {
    test: /^(?:([\w.]+)\.)?email\s+should not be empty$/,
    build: (m) => {
      const path = m[1] ? `${m[1]}.email` : 'email';
      return {
        friendly: `O campo «${humanizePath(path)}» é obrigatório.`,
        field: path,
      };
    },
  },
  // 6) email com formato inválido.
  {
    test: /^(?:([\w.]+)\.)?email\s+must be an email$/,
    build: (m) => ({
      friendly: 'E-mail inválido. Verifique o formato (ex.: nome@exemplo.com).',
      field: m[1] ? `${m[1]}.email` : 'email',
    }),
  },
  // 7) Data ISO inválida.
  {
    test: /^(?:([\w.]+)\.)?(\w+)\s+must be a valid ISO 8601 date string$/,
    build: (m) => {
      const child = m[2] ?? '';
      const path = m[1] ? `${m[1]}.${child}` : child;
      return {
        friendly: `Data inválida em «${humanizePath(path)}». Use o formato AAAA-MM-DD.`,
        field: path,
      };
    },
  },
  // 8) Campo obrigatório vazio (genérico).
  {
    test: /^(?:([\w.]+)\.)?(\w+)\s+should not be empty$/,
    build: (m) => {
      const child = m[2] ?? '';
      const path = m[1] ? `${m[1]}.${child}` : child;
      return {
        friendly: `O campo «${humanizePath(path)}» é obrigatório.`,
        field: path,
      };
    },
  },
  // 9) Enum: must be one of the following values: ...
  {
    test: /^(?:([\w.]+)\.)?(\w+)\s+must be one of the following values:\s+(.+)$/,
    build: (m) => {
      const child = m[2] ?? '';
      const path = m[1] ? `${m[1]}.${child}` : child;
      return {
        friendly: `Valor inválido para «${humanizePath(path)}». Opções aceitas: ${m[3]}.`,
        field: path,
      };
    },
  },
  // 10) Número fora do limite.
  {
    test: /^(?:([\w.]+)\.)?(\w+)\s+(must be a number|must not be less than|must be a positive)/,
    build: (m) => {
      const child = m[2] ?? '';
      const path = m[1] ? `${m[1]}.${child}` : child;
      return {
        friendly: `Número inválido em «${humanizePath(path)}». Verifique o valor.`,
        field: path,
      };
    },
  },
  // 11) URL inválida.
  {
    test: /^(?:([\w.]+)\.)?(\w+)\s+must be a URL/,
    build: (m) => {
      const child = m[2] ?? '';
      const path = m[1] ? `${m[1]}.${child}` : child;
      return {
        friendly: `URL inválida em «${humanizePath(path)}». Inclua http:// ou https://.`,
        field: path,
      };
    },
  },
  // 12) Boolean.
  {
    test: /^(?:([\w.]+)\.)?(\w+)\s+must be a boolean value$/,
    build: (m) => {
      const child = m[2] ?? '';
      const path = m[1] ? `${m[1]}.${child}` : child;
      return {
        friendly: `Valor inválido em «${humanizePath(path)}». Use sim ou não.`,
        field: path,
      };
    },
  },
];

function fallbackByStatus(status: number | undefined): string {
  if (status === undefined) {
    return 'Não foi possível salvar as informações no momento. Tente novamente.';
  }
  if (status === 401) {
    return 'Sua sessão expirou. Faça login novamente para continuar.';
  }
  if (status === 403) {
    return 'Você não tem permissão para realizar esta ação.';
  }
  if (status === 404) {
    return 'Não encontramos o condomínio. Recarregue a página.';
  }
  if (status === 409) {
    return 'Esses dados já estão em uso. Verifique e tente novamente.';
  }
  if (status >= 400 && status < 500) {
    return 'Não conseguimos validar suas informações. Confira os dados preenchidos.';
  }
  if (status >= 500) {
    return 'Estamos com um problema momentâneo. Tente novamente em instantes.';
  }
  return 'Não foi possível salvar as informações no momento. Tente novamente.';
}

/**
 * Mapeia uma mensagem técnica do NestJS ValidationPipe para uma mensagem
 * amigável em PT-BR. Quando há múltiplas mensagens, retorna a primeira mapeada
 * (UX) e preserva todas em `technical` (Sentry).
 */
export function mapNestErrorToFriendly(
  raw: string | string[] | Record<string, unknown> | undefined | null,
  status?: number,
): FriendlyError {
  const technical = normalizeRaw(raw);

  for (const message of technical) {
    for (const rule of rules) {
      const match = message.match(rule.test);
      if (match) {
        const result = rule.build(match);
        return {
          friendly: result.friendly,
          technical,
          field: result.field,
          suggestion: result.suggestion,
        };
      }
    }
  }

  // Sem match: usa fallback genérico por status, mas preserva técnico no Sentry.
  return {
    friendly: fallbackByStatus(status),
    technical,
  };
}
